import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import { helpContext } from '../../context/HelpContext';
import { createArticleBreadcrumbs, createArticleUrl, createTopicsUrl } from "../../helpers/helpCenterUtils.js"
import { useHelpCenterRights } from "../../hooks/useHelpCenterRights";
import ArticleEditor from './ArticleEditor';
import BackToTopicsButton from "./BackToTopicsButton";
import HelpBreadcrumbs from "./HelpBreadcrumbs";

export default function ArticlePage() {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const { id } = useParams();

  const { canEdit } = useHelpCenterRights();

  const {
    allArticles, allTopics, loading,
    createArticle, createArticleLoading,
    updateArticle, updateArticleLoading
  } = useContext(helpContext);

  const [currentArticle, setCurrentArticle] = useState(null);
  const [editState, setEditState] = useState();

  const breadcrumbData = useMemo(() => (
    currentArticle?.articleId
      ? createArticleBreadcrumbs(allTopics, currentArticle.articleId)
      : []
  ), [allTopics, currentArticle]);

  const draftArticle = useMemo(() => currentArticle || {
    id,
    title: '',
    description: '',
    content: '',
    tags: [],
    related: [],
  }, [currentArticle, id]);

  const handleEditClick = () => {
    setEditState((prev) => ({
      ...prev,
      isCreateMode: false,
      isEditing: true,
    }));
  };

  const handleSave = async (article) => {
    let articleInput = article;

    if (editState.isCreateMode) {
      articleInput = {
        ...articleInput,
        id: 0,
        topicId: editState.parentTopicId,
      };
    }

    delete articleInput.articleId;
    delete articleInput.index;
    delete articleInput.type;

    let updated =
      editState.isCreateMode
        ? await createArticle(articleInput)
        : await updateArticle(articleInput);

    if (!updated) {
      console.error("** Failed to save article");
      return;
    }

    leaveEditState();
  };

  const leaveEditState = () => {
    if (editState.returnToTopicsPage) {
      history.push(createTopicsUrl());
    }
    else {
      setEditState({
        isCreateMode: false,
        isEditing: false,
      });
    }
  };

  useEffect(() => {
    const found = allArticles.find(a => a.articleId == id);
    setCurrentArticle(found || null);

    const isEdit = Boolean(location.state?.edit);

    setEditState({
      isCreateMode: isEdit && (Number(id) === 0),
      isEditing: isEdit,
      parentTopicId: location.state?.parentTopicId,
      returnToTopicsPage: Boolean(location.state?.edit),
    });
  }, [allArticles, id]);

  const relatedArticles = useMemo(() => (
    currentArticle?.related
      ? allArticles.filter(a => currentArticle.related.includes(a.articleId))
      : []
  ), [allArticles, currentArticle]);

  ////const categories = useMemo(() => {
  ////  const catSet = new Set();
  ////  allArticles.forEach(a => a.categoryPath?.forEach(c => catSet.add(c)));
  ////  return Array.from(catSet).sort();
  ////}, [allArticles]);

  const RelatedArticles = ({ articles }) => {
    if (!articles.length) return null;
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Relaterade artiklar
        </Typography>
        {articles.map(ra => (
          <Box
            key={ra.articleId}
            onClick={() => history.push(createArticleUrl(ra.articleId))}
            sx={{
              cursor: 'pointer',
              mb: 1,
              color: 'primary.main',
              textDecoration: 'underline'
            }}
          >
            {ra.title}
          </Box>
        ))}
      </Box>
    );
  };

  if (loading || !editState) {
    return (
      <Box sx={{ p: 1 }}>
        <FormattedMessage id="helpCenter.article.loading" />
      </Box>
    );
  }

  if (editState.isEditing && currentArticle || editState.isCreateMode) {
    return (
      <ArticleEditor
        article={draftArticle}
        //categories={categories}
        createMode={editState.isCreateMode}
        onCancel={leaveEditState}
        onSave={handleSave}
        saving={createArticleLoading || updateArticleLoading}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', py: 4, px: 2 }}>
      <BackToTopicsButton />
      <HelpBreadcrumbs breadcrumbData={breadcrumbData} />

      {!currentArticle ? (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            <FormattedMessage id="helpCenter.article.missing" />
          </Typography>
          {false && (
            <Button variant="outlined" /*onClick={() => setIsEditing(true)}*/>
              <FormattedMessage id="helpCenter.article.create" />
            </Button>
          )}
        </>
      ) : (
        <>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={isMobile ? 1 : 0}
            mb={2}
          >
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {currentArticle.title}
            </Typography>
            {canEdit &&
              <Button variant="outlined" onClick={handleEditClick}>
                <FormattedMessage id="btn.edit" />
              </Button>
            }
          </Stack>

          <Typography variant="subtitle1" sx={{ mb: 3, color: '#555' }}>
            {currentArticle.description}
          </Typography>

          <Box
            sx={{
              mb: 4,
              '& p': { mb: 2 },
              '& ol': { pl: 3, mb: 2 },
              '& li': { mb: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: currentArticle.content }}
          />

          <Box sx={{ mb: 4 }}>
            {currentArticle.tags?.map(tag => (
              <Chip key={tag} label={tag} variant="outlined" sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>

          <RelatedArticles articles={relatedArticles} />
        </>
      )}
    </Box>
  );
}
